/**
 * This is the main page after the use logs in
 */
import * as React from 'react';
import { Page } from './base/page';
import { Heading1, Heading4, Paragraph, Heading3, Heading2 } from '../ap/base/text';
import * as gls from '../ap/layout/gls';
import * as typestyle from 'typestyle';
import * as styles from '../ap/base/styles';
import { route, navigateTo, redirectToRegistration } from '../links';
import { Hr } from '../ap/base/hr';
import { MaxWidthReef, SectionStyles } from '../ap/pages/sections';
import { LandingPageSection, LandingPageTalky, LandingPageHeadingSecondary, LandingPageText, LandingPageHeadingPrimary, LandingPageHero } from '../ap/pages/landing';
import { VoiIcon, DocumentStoreIcon, DocumentSightingIcon } from '../icons/appIcons';
import { SecondaryButton, PrimaryButton } from '../ap/base/button';
import { Anchor } from '../ap/base/anchor';
import { config } from '../../app/config/config';
import * as csx from 'csstips';
import { style } from 'typestyle';
import { trackLandingPage, trackLinkClick } from '../ap/omniture/omnitureService';

declare var propertyTransferBaseUrl: string;

const images = {
  hero: propertyTransferBaseUrl + '/images/banner.jpg',
  works: propertyTransferBaseUrl + '/images/works.png',
  start: propertyTransferBaseUrl + '/images/start.png',
  pricing: propertyTransferBaseUrl + '/images/pricing.png',

  /** Named images */
  arnecc: propertyTransferBaseUrl + '/images/arnecc.png',
  verify: propertyTransferBaseUrl + '/images/verify.png',
  safe: propertyTransferBaseUrl + '/images/safe.png',
  namedImageSize: { width: '296px', height: '204px' }
}

/** Hero image details */
const heroImageDetails = {
  url: images.hero,
  imageMediaQueries: {
    $nest: {
      '@media screen and (max-width: 1036px)': {
        width: '120%',
      },
      '@media screen and (max-width: 890px)': {
        width: '150%',
      },
      '@media screen and (max-width: 680px)': {
        width: '200%',
      },
      '@media screen and (max-width: 500px)': {
        width: '250%',
      },
      '@media screen and (max-width: 400px)': {
        width: '350%',
      }
    }
  }
};

const NamedImage = ({ name, url }: { name: string, url: string }) => {
  return (
    <div style={csx.horizontallyCenterChildren}>
      <div className={style(csx.inlineBlock, { borderRadius: '3px', backgroundColor: 'white' })}>
        <gls.Content className={style(csx.layerParent, { textAlign: 'left' }, images.namedImageSize)}>
          <img src={url} className={style(csx.attachToLayerParent)} />
        </gls.Content>
        <gls.Content className={style(csx.padding(20), { width: images.namedImageSize.width })}>
          <Heading4 text={name} />
        </gls.Content>
      </div>
    </div>
  );
}

export const Landing = ({}) => {
  trackLandingPage();
  const omnitureOnClickOnlineForm = () => trackLinkClick('diy:landing', 'tile online payment form');
  const omnitureOnClickSignUp = () => trackLinkClick('diy:landing', 'tile sign up');

  const loginButtonClick = (isBanner: boolean): void => {
    trackLinkClick('diy:landing',isBanner ? 'banner login' : 'tile login');
    window.location.replace(config.portalConfig.portalLoginUrl);
  };

  return (
    <Page
      loggedIn={false}
      seamless={true}
      sections={[
        <LandingPageHero key="hero"
          imgUrl={heroImageDetails.url}
          imgMediaClassName={typestyle.style(heroImageDetails.imageMediaQueries)}
          maxWidth={styles.maxLandingPageWidth + 'px'}>
          <LandingPageTalky maxWidth='410px' position={'right'} moveTalkyOutAtWidth={1065}>
            <LandingPageHeadingSecondary text="Verification of Identity" />
            <LandingPageHeadingPrimary text="Property Transfers" />
            <gls.SmallVerticalSpace />
            <LandingPageText>
              <span>A new do it yourself verification of identity tool for the conveyancing industry.</span>
            </LandingPageText>
            <gls.SmallVerticalSpace />
            <PrimaryButton id="login1" onClick={() => loginButtonClick(true)} text="Log in" />
          </LandingPageTalky>
        </LandingPageHero>,
        <LandingPageSection key="keypoints" backgroundColor={styles.colors.warmGrey1}>
          <gls.ContentVerticalCentered>
            <gls.Content className={style(csx.maxWidth('100%'), csx.horizontallyCenterChildren)}>
              <gls.Content className={style(csx.maxWidth(900), csx.horizontallyCenterChildren)}>
                <Heading3 text="A fast, simple and secure VOI for your client, without leaving the office." />
              </gls.Content>
            </gls.Content>
            <gls.SmallVerticalSpace space={40} />
            <gls.ResponsiveGridParent breakpoint={1000} margin={30}>
              <NamedImage name="Ensure ARNECC compliance for yourself and your clients." url={images.arnecc} />
              <NamedImage name="Verify clients where and when you need to." url={images.verify} />
              <NamedImage name="Access all your VOI reports anytime." url={images.safe} />
            </gls.ResponsiveGridParent>
            <gls.SmallVerticalSpace space={40} />
          </gls.ContentVerticalCentered>
        </LandingPageSection>,
        <LandingPageSection key="focus" backgroundColor={'white'}>
          <gls.ResponsiveGridParent breakpoint={750}>
            <gls.VerticalMargined style={{ paddingRight: '25px' }}>
              <Heading1 text="How it works" />
              <LandingPageText>
                <span>Using the VOI tool, you can enter essential client details, take copies of your client’s documents and verify their presence using their mobile phone, binding them to the VOI check. You can even add a photo of your client if you wish.</span>
              </LandingPageText>
              <LandingPageText>
                <span>Once submitted we produce a VOI Report containing key client details and we store the VOI reports just in case you need them in the future.</span>
              </LandingPageText>
            </gls.VerticalMargined>
            <div className={style(csx.horizontallyCenterChildren)}>
              <img src={images.works} style={{ maxWidth: '90%', maxHeight: '100%' }} />
            </div>
          </gls.ResponsiveGridParent>
        </LandingPageSection>,
        <LandingPageSection key="how" backgroundColor={styles.colors.warmGrey1}>
          <gls.ResponsiveGridParent breakpoint={750}>
            <div className={style(csx.padding(0, 0, 24, 0), csx.horizontallyCenterChildren)}>
              <img src={images.start} className={style(csx.maxWidth('80%'))} />
            </div>
            <gls.VerticalMargined>
              <Heading1 text="How do I start?" />
              <LandingPageText>
                <span>
                  Registering for Australia Post's do it yourself VOI tool is easy. Complete the 
                  <Anchor id="registration" href={redirectToRegistration()} target="_blank" onClick={omnitureOnClickOnlineForm}><span>online registration form</span></Anchor>
                  to register. You will then receive a welcome pack, containing your login details to access the tool and the VOI Report Portal.
                </span>
              </LandingPageText>
              <LandingPageText>
                <span>To then register for our over the counter VOI service, sign and return the Referral Agreement included in your welcome pack. You will then be assigned a unique client VOI Form, and further information to provide to your clients when using our service.</span>
              </LandingPageText>
              <Anchor id="signuplink" href={redirectToRegistration()} target="_blank" onClick={omnitureOnClickSignUp}><PrimaryButton id="signup" text="Sign up"/></Anchor>
            </gls.VerticalMargined>
          </gls.ResponsiveGridParent>
        </LandingPageSection>,
        <LandingPageSection key="start" backgroundColor={'white'}>
          <gls.ResponsiveGridParent breakpoint={750}>
            <gls.VerticalMargined>
                <Heading1 text="Pricing" />
                <LandingPageText><span>Do it yourself Verification Of <br/>Identity check ${config.servicePrice} (inc. GST) </span></LandingPageText>
              <PrimaryButton id="login2" onClick={() => loginButtonClick(false)} text="Log in" />
            </gls.VerticalMargined>
            <div className={style(csx.horizontallyCenterChildren)}>
              <img src={images.pricing} style={{ maxWidth: '90%', maxHeight: '100%' }} />
            </div>
          </gls.ResponsiveGridParent>
        </LandingPageSection>
      ]} />
  );
}
